import { call } from '@advanza/api'
import { SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Icon, LoadingDots } from '@advanza/ui'
import {
    OutstandingInvoice,
    OutstandingInvoicesResponse,
    OutstandingProvider,
    OutstandingTotals,
} from '@trustoo/types/api/controllers/Invoices'
import clsx from 'clsx'
import { formatEuro } from 'components/billing/debtorNew/format'
import { AgeBadge, Card, DueBadge, StatusPill } from 'components/billing/debtorNew/ui'
import DatePicker from 'components/DatePicker'
import SearchProOrInvoice from 'components/misc/SearchProOrInvoice'
import Pagination from 'components/Pagination'
import { format } from 'date-fns'
import queryString from 'query-string'
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

const PAGE_SIZE_OPTIONS = [25, 50, 100]
const COL_COUNT = 9

type SortField = 'amountOpen' | 'oldest' | 'count' | 'providerId'
type SortDir = 'asc' | 'desc'

const STATUS_OPTIONS = [
    { value: 'all', name: 'All statuses' },
    { value: 'noStatus', name: 'No status' },
    { value: 'charged_back', name: 'Charged back' },
    { value: 'failed', name: 'Failed' },
    { value: 'payment_failed', name: 'Payment failed' },
    { value: 'expired', name: 'Expired' },
    { value: 'payment_plan', name: 'Payment plan' },
    { value: 'debt_collection', name: 'Debt collection' },
    { value: 'manualAll', name: 'Manual transfer' },
]

const SORT_OPTIONS = [
    { value: 'amountOpen', name: 'Amount open' },
    { value: 'oldest', name: 'Oldest invoice' },
    { value: 'count', name: 'Nr of invoices' },
    { value: 'providerId', name: 'Provider ID' },
]

interface Filters {
    dateFrom: string
    dateTo: string
    status: string
    providerId: string
}

const emptyTotals: OutstandingTotals = {
    providers: 0,
    invoices: 0,
    amount: 0,
    creditCount: 0,
    creditAmount: 0,
}

function useOutstanding(
    country: string,
    filters: Filters,
    sort: SortField,
    sortDir: SortDir,
    page: number,
    pageSize: number,
) {
    const [providers, setProviders] = useState<OutstandingProvider[]>([])
    const [invoices, setInvoices] = useState<OutstandingInvoice[]>([])
    const [totals, setTotals] = useState<OutstandingTotals>(emptyTotals)
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        if (!country) {
            return
        }
        // Ignore-flag so a slow response for old filters can't clobber a newer one.
        let ignore = false
        setIsLoading(true)
        setHasError(false)
        call('office/invoices/get-outstanding-invoices', {
            query: {
                country,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                status: filters.status,
                providerId: filters.providerId,
                sort,
                sortDir,
                pageNr: page,
                pageSize,
            },
        }).then(
            (response: OutstandingInvoicesResponse) => {
                if (ignore) {
                    return
                }
                setProviders(response.providers || [])
                setInvoices(response.invoices || [])
                setTotals(response.totals || emptyTotals)
                setIsLoading(false)
            },
            () => {
                if (ignore) {
                    return
                }
                setHasError(true)
                setIsLoading(false)
            },
        )
        return () => {
            ignore = true
        }
    }, [
        country,
        filters.dateFrom,
        filters.dateTo,
        filters.status,
        filters.providerId,
        sort,
        sortDir,
        page,
        pageSize,
    ])

    return { providers, invoices, totals, isLoading, hasError }
}

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-neutral-500">{label}</span>
        {children}
    </label>
)

interface Props {
    country: string
}

const OutstandingTab = ({ country }: Props) => {
    // Filters, sort, and page live in the URL so views are shareable and survive a refresh.
    const [searchParams, setSearchParams] = useSearchParams()
    const filters: Filters = {
        dateFrom: searchParams.get('dateFrom') ?? '',
        dateTo: searchParams.get('dateTo') ?? '',
        status: searchParams.get('status') ?? 'all',
        providerId: searchParams.get('providerId') ?? '',
    }
    const sortParam = searchParams.get('sort')
    const sort: SortField = SORT_OPTIONS.some((option) => option.value === sortParam)
        ? (sortParam as SortField)
        : 'amountOpen'
    const sortDir: SortDir = searchParams.get('dir') === 'asc' ? 'asc' : 'desc'
    const page = Math.max(Number(searchParams.get('page')) || 0, 0)
    const sizeParam = Number(searchParams.get('size'))
    const pageSize = PAGE_SIZE_OPTIONS.includes(sizeParam) ? sizeParam : 25

    const setParams = (changes: Record<string, string>, resetPage = true) => {
        setSearchParams(
            (prev) => {
                Object.entries(changes).forEach(([key, value]) => {
                    if (value === '' || value === 'all') {
                        prev.delete(key)
                    } else {
                        prev.set(key, value)
                    }
                })
                if (resetPage) {
                    prev.delete('page')
                }
                return prev
            },
            { replace: true },
        )
    }

    const [providerName, setProviderName] = useState('')
    const [collapsed, setCollapsed] = useState<Record<number, boolean>>({})
    const { providers, invoices, totals, isLoading, hasError } = useOutstanding(
        country,
        filters,
        sort,
        sortDir,
        page,
        pageSize,
    )
    const count = totals.providers

    const changeFilter = (changes: Partial<Filters>) => {
        setParams(changes as Record<string, string>)
    }

    const invoicesByProvider = useMemo(() => {
        const map: Record<number, OutstandingInvoice[]> = {}
        invoices.forEach((invoice) => {
            ;(map[invoice.service_provider_id] ??= []).push(invoice)
        })
        return map
    }, [invoices])

    const { protocol, hostname } = window.location
    const exportQuery: Record<string, string> = { country }
    if (filters.dateFrom) {
        exportQuery.dateFrom = filters.dateFrom
    }
    if (filters.dateTo) {
        exportQuery.dateTo = filters.dateTo
    }
    if (filters.status && filters.status !== 'all') {
        exportQuery.status = filters.status
    }
    if (filters.providerId) {
        exportQuery.providerId = filters.providerId
    }
    const exportHref = `${protocol}//${hostname}/api/office/invoices/export-outstanding-invoices?${queryString.stringify(exportQuery)}`

    return (
        <div className="space-y-6">
            <Card className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <Field label="Invoice date from">
                        <DatePicker
                            onChange={(e: { target: { value: string } }) =>
                                changeFilter({ dateFrom: e.target.value || '' })
                            }
                            format="YYYY-MM-DD"
                            value={filters.dateFrom}
                            placeholder="From"
                        />
                    </Field>
                    <Field label="Invoice date to">
                        <DatePicker
                            onChange={(e: { target: { value: string } }) =>
                                changeFilter({ dateTo: e.target.value || '' })
                            }
                            format="YYYY-MM-DD"
                            value={filters.dateTo}
                            placeholder="To"
                        />
                    </Field>
                    <Field label="Status">
                        <SelectSimple
                            options={STATUS_OPTIONS}
                            value={filters.status}
                            onChange={(e: SelectSimpleChangeEvent) =>
                                changeFilter({ status: String(e.target.value ?? 'all') })
                            }
                            alwaysControlValue
                        />
                    </Field>
                    <Field label="Debtor">
                        {filters.providerId ? (
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-md text-neutral-700"
                                onClick={() => {
                                    setProviderName('')
                                    changeFilter({ providerId: '' })
                                }}>
                                <Icon name="close" fontSize={16} />
                                {providerName || `Provider #${filters.providerId}`}
                            </button>
                        ) : (
                            <div className="min-w-64">
                                <SearchProOrInvoice
                                    onSelect={(provider: {
                                        service_provider_id: number
                                        business_name: string
                                    }) => {
                                        setProviderName(provider.business_name)
                                        changeFilter({
                                            providerId: String(provider.service_provider_id),
                                        })
                                    }}
                                />
                            </div>
                        )}
                    </Field>
                    <Field label="Sort by">
                        <div className="flex items-center gap-1">
                            <SelectSimple
                                options={SORT_OPTIONS}
                                value={sort}
                                onChange={(e: SelectSimpleChangeEvent) =>
                                    setParams({ sort: String(e.target.value ?? 'amountOpen') })
                                }
                                alwaysControlValue
                            />
                            <button
                                type="button"
                                className="rounded-lg border border-neutral-300 bg-white p-2 text-neutral-600 hover:bg-neutral-50"
                                title={sortDir === 'desc' ? 'Descending' : 'Ascending'}
                                onClick={() =>
                                    setParams({ dir: sortDir === 'desc' ? 'asc' : 'desc' })
                                }>
                                <Icon
                                    name={sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}
                                    fontSize={16}
                                />
                            </button>
                        </div>
                    </Field>
                    <div className="ml-auto">
                        <a
                            href={exportHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-md font-medium text-neutral-700 hover:bg-neutral-50">
                            <Icon name="download" fontSize={16} />
                            Export CSV
                        </a>
                    </div>
                </div>
            </Card>

            <div className="flex items-center gap-6 text-md">
                <span className="text-neutral-500">
                    <span className="font-semibold text-dark">{count.toLocaleString('nl-NL')}</span>{' '}
                    debtor{count === 1 ? '' : 's'} ·{' '}
                    <span className="font-semibold tabular-nums text-dark">
                        {totals.invoices.toLocaleString('nl-NL')}
                    </span>{' '}
                    open invoices
                </span>
                <span className="text-neutral-500">
                    Total open:{' '}
                    <span className="font-semibold tabular-nums text-dark">
                        {formatEuro(totals.amount)}
                    </span>
                </span>
                {totals.creditCount > 0 && (
                    <span className="text-neutral-500">
                        Open credit notes:{' '}
                        <span className="font-semibold tabular-nums text-green-700">
                            {formatEuro(totals.creditAmount)}
                        </span>{' '}
                        ({totals.creditCount.toLocaleString('nl-NL')})
                    </span>
                )}
                {isLoading && (
                    <span className="text-neutral-400">
                        Loading <LoadingDots />
                    </span>
                )}
            </div>

            {hasError ? (
                <p className="text-red-700">Failed loading outstanding invoices.</p>
            ) : (
                <Card className="overflow-hidden">
                    <table className="w-full border-collapse text-md">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-50 text-sm uppercase tracking-wide text-neutral-500">
                                <th className="px-4 py-3 text-left font-semibold">Debtor</th>
                                <th className="px-4 py-3 text-left font-semibold">Date</th>
                                <th className="px-4 py-3 text-left font-semibold">Invoice #</th>
                                <th className="px-4 py-3 text-left font-semibold">About</th>
                                <th className="px-4 py-3 text-left font-semibold">Description</th>
                                <th className="px-4 py-3 text-right font-semibold">Amount</th>
                                <th className="px-4 py-3 text-right font-semibold">Open</th>
                                <th className="px-4 py-3 text-left font-semibold">Due</th>
                                <th className="px-4 py-3 text-left font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.map((provider) => {
                                const isCollapsed = !!collapsed[provider.providerId]
                                const providerInvoices =
                                    invoicesByProvider[provider.providerId] || []
                                return (
                                    <Fragment key={provider.providerId}>
                                        <tr
                                            className="cursor-pointer border-t-2 border-neutral-200 bg-neutral-50 hover:bg-neutral-100"
                                            onClick={() =>
                                                setCollapsed((prev) => ({
                                                    ...prev,
                                                    [provider.providerId]:
                                                        !prev[provider.providerId],
                                                }))
                                            }>
                                            <td className="px-4 py-3" colSpan={5}>
                                                <div className="flex items-center gap-2">
                                                    <Icon
                                                        name={
                                                            isCollapsed
                                                                ? 'chevron_right'
                                                                : 'expand_more'
                                                        }
                                                        fontSize={18}
                                                        grey
                                                    />
                                                    <Link
                                                        to={`/service/providers/provider/${provider.providerId}/company`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="font-semibold text-dark hover:text-primary-700">
                                                        {provider.businessName}
                                                    </Link>
                                                    <span className="text-sm text-neutral-400">
                                                        #{provider.providerId}
                                                    </span>
                                                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-sm font-medium text-neutral-600">
                                                        {provider.count} open
                                                    </span>
                                                    {provider.creditCount > 0 && (
                                                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-sm font-medium tabular-nums text-green-700">
                                                            {provider.creditCount} credit ·{' '}
                                                            {formatEuro(provider.creditAmount)}
                                                        </span>
                                                    )}
                                                    <AgeBadge days={provider.oldestDays} />
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums text-neutral-500">
                                                {formatEuro(provider.amount)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-semibold tabular-nums text-dark">
                                                {formatEuro(provider.amountOpen)}
                                            </td>
                                            <td className="px-4 py-3" colSpan={2} />
                                        </tr>
                                        {!isCollapsed &&
                                            providerInvoices.map((invoice) => (
                                                <tr
                                                    key={invoice.invoice_id}
                                                    className="border-t border-neutral-100 hover:bg-neutral-50">
                                                    <td className="px-4 py-2.5" />
                                                    <td className="px-4 py-2.5 tabular-nums text-neutral-600">
                                                        {invoice.datetime
                                                            ? format(
                                                                  new Date(invoice.datetime),
                                                                  'd MMM yyyy',
                                                              )
                                                            : ''}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-neutral-700">
                                                        <span className="flex items-center gap-1.5">
                                                            {invoice.invoice_nr}
                                                            {invoice.amount < 0 && (
                                                                <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-sm font-medium text-green-700">
                                                                    credit
                                                                </span>
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-neutral-500">
                                                        {invoice.about_month}
                                                    </td>
                                                    <td
                                                        className="max-w-xs truncate px-4 py-2.5 text-neutral-500"
                                                        title={invoice.description}>
                                                        {invoice.description}
                                                    </td>
                                                    <td
                                                        className={clsx(
                                                            'px-4 py-2.5 text-right tabular-nums',
                                                            invoice.amount < 0
                                                                ? 'text-green-700'
                                                                : 'text-neutral-600',
                                                        )}>
                                                        {formatEuro(invoice.amount)}
                                                    </td>
                                                    <td
                                                        className={clsx(
                                                            'px-4 py-2.5 text-right tabular-nums',
                                                            invoice.amount < 0
                                                                ? 'text-green-700'
                                                                : 'text-dark',
                                                        )}>
                                                        {formatEuro(invoice.amount_open)}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <DueBadge
                                                            daysOverdue={invoice.days_overdue}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <StatusPill status={invoice.status} />
                                                    </td>
                                                </tr>
                                            ))}
                                    </Fragment>
                                )
                            })}
                            {!isLoading && providers.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={COL_COUNT}
                                        className="py-10 text-center text-neutral-400">
                                        No outstanding invoices for these filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>
            )}

            <div className="flex flex-wrap items-center gap-4">
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    count={count}
                    onPageChange={(nextPage: number) =>
                        setParams({ page: String(nextPage) }, false)
                    }
                />
                <label className="flex items-center gap-2 text-md text-neutral-500">
                    Per page
                    <SelectSimple
                        options={PAGE_SIZE_OPTIONS.map((size) => ({
                            value: String(size),
                            name: String(size),
                        }))}
                        value={String(pageSize)}
                        onChange={(e: SelectSimpleChangeEvent) =>
                            setParams({ size: String(e.target.value ?? '25') })
                        }
                        alwaysControlValue
                    />
                </label>
            </div>
            <p className="text-sm text-neutral-400">
                The bold open amount per debtor is net: open invoices minus open credit notes (the
                grey amount is gross). Payt partial payments are not deducted. Amounts ≤ €2 and
                invoices with a stale paid status are excluded. The header total stays gross, with
                credit notes shown separately.
            </p>
        </div>
    )
}

export default OutstandingTab
